import { useChart } from '@chakra-ui/charts';
import { Box, For, HStack, Text, useToken, VStack } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { Tooltip as CustomTooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectLanguage } from '@redux/settings/settings.selectors';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip,
  TooltipContentProps,
  XAxis,
  XAxisTickContentProps,
  YAxis,
} from 'recharts';

import { ChronoUnits, DateRangeDroneCountType, DRONE_STATS_NO_PLATFORM_CODE } from '@/types/data/statistic.types';

type Props = {
  data: DateRangeDroneCountType[];
  getPlatformColor: (value: string) => string;
};

function getLabelByUnit(dateTime: string, unit: ChronoUnits, locale: string) {
  const date = new Date(dateTime);
  switch (unit) {
    case ChronoUnits.YEARS:
      return date.getFullYear();
    case ChronoUnits.MONTHS:
      return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).slice(0, 3).toLocaleUpperCase();
    case ChronoUnits.WEEKS:
    case ChronoUnits.DAYS:
      return format(date, 'dd/MM');
    case ChronoUnits.HALF_DAYS:
    case ChronoUnits.HOURS:
      return format(date, "HH'h");
  }
}

const CustomGraphTooltip = ({ active, payload, label }: TooltipContentProps) => {
  const isVisible = active && payload.length !== 0;
  if (!isVisible || label === undefined) {
    return;
  }
  return (
    <VStack gap={1} paddingY={1} paddingX={1.5} backgroundColor="neutral.black">
      <Text alignSelf="start">{label.toString().split('_')[0]}</Text>
      <VStack gap={0.25} width="100%" alignItems="start">
        <For each={payload.filter((entry) => entry.dataKey?.toString().includes('platforms'))}>
          {(entry) => (
            <Text key={entry.dataKey as string}>
              <FormattedMessage
                id="components.notificationSearch.reporting.values"
                values={{ name: entry.name, count: entry.value }}
              />
            </Text>
          )}
        </For>
      </VStack>
      <VStack gap={0.25} width="100%" alignItems="start">
        <For each={payload.filter((entry) => entry.dataKey?.toString().includes('classifications'))}>
          {(entry) => (
            <Text key={entry.dataKey as string}>
              <FormattedMessage
                id="components.notificationSearch.reporting.values"
                values={{ name: entry.name, count: entry.value }}
              />
            </Text>
          )}
        </For>
      </VStack>
    </VStack>
  );
};

const CustomTickLabel = (
  props: XAxisTickContentProps,
  data: {
    name: string;
    total: number;
    unit: ChronoUnits;
    year: number;
    platforms: {
      [k: string]: number;
    };
    levels: {
      [k: string]: number;
    };
  }[],
  isTiltedTickLabel: boolean,
) => {
  const [neutral300, neutral500] = useToken('colors', ['neutral.300', 'neutral.500']);

  const { x, y, payload, index, angle } = props;

  const current = data[index];
  const prev = index > 0 ? data[index - 1] : undefined;

  const isFirstOfYear = prev?.year !== current.year;

  return (
    <g transform={`translate(${x},${y}) rotate(${isTiltedTickLabel ? angle : 0})`} key={payload.value}>
      <text textAnchor={isTiltedTickLabel ? 'end' : 'middle'} fill={neutral300} y={10}>
        {payload.value.split('_')[0]}
      </text>
      {isFirstOfYear && current.unit === ChronoUnits.MONTHS && (
        <text x={isTiltedTickLabel ? 0 : 21} y={isTiltedTickLabel ? 23 : 28} textAnchor="end" fill={neutral500}>
          {current.year}
        </text>
      )}
    </g>
  );
};

type LegendProps = {
  platforms: { code: string; name: string }[];
  getPlatformColor: (platform: string) => string;
};
const CustomLegend = ({ platforms, getPlatformColor }: Readonly<LegendProps>) => {
  return (
    <HStack gap={2} width="100%" justifyContent="center" flexWrap="wrap" rowGap={1}>
      {platforms.map(({ code, name }) => {
        return (
          <HStack key={code} gap={1} flexShrink={0}>
            <Box
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: getPlatformColor(code),
                flexShrink: 0,
              }}
            />
            <CustomTooltip content={name}>
              <Text textStyle="sm" lineClamp={1}>
                {name.slice(0, 4)}
              </Text>
            </CustomTooltip>
          </HStack>
        );
      })}
    </HStack>
  );
};

export default function DroneDetectionByPlatformsGraph({ data, getPlatformColor }: Readonly<Props>) {
  const locale = useAppSelector(selectLanguage);
  const { formatMessage } = useIntl();
  const [neutral300, neutral700] = useToken('colors', ['neutral.300', 'neutral.700']);

  const chart = useChart({
    data: data.map((entry) => ({
      name: `${getLabelByUnit(entry.dateTime, entry.unit, locale)}_${entry.dateTime}`,
      total: entry.count,
      unit: entry.unit,
      year: new Date(entry.dateTime).getFullYear(),
      platforms: { ...Object.fromEntries(entry.countByPlatform.map(({ platform, count }) => [platform, count])) },
      levels: {
        ...Object.fromEntries(entry.countByLevel.map(({ level, count }) => [level, count])),
      },
    })),
  });

  const platforms = Array.from(
    data
      .reduce((acc, item) => {
        item.countByPlatform.forEach((p) => {
          acc.set(p.platform, {
            code: p.platform,
            name:
              p.platform === DRONE_STATS_NO_PLATFORM_CODE
                ? formatMessage({
                    id: 'components.notificationSearch.reporting.noPlatform',
                  })
                : p.platformName,
          });
        });

        return acc;
      }, new Map<string, { code: string; name: string }>())
      .values(),
  ).toSorted((a, b) => a.name.localeCompare(b.name));

  const isTiltedTickLabel = chart.data.length > 12;

  return (
    <ChartWrapper
      width="616px"
      isEmpty={chart.getTotal('total') === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage(
        { id: 'components.notificationSearch.reporting.nbDronesInAlertZones' },
        {
          unit: formatMessage({ id: `components.notificationSearch.reporting.unit.${chart.data[0]?.unit}` }),
        },
      )}
    >
      <BarChart
        style={{ width: '100%', height: '100%' }}
        margin={{ bottom: isTiltedTickLabel ? 20 : 10, top: 5 }}
        data={chart.data}
        barSize={16}
      >
        <CartesianGrid strokeWidth={2} stroke={neutral700} vertical={false} />
        <Tooltip cursor={{ fill: neutral700 }} content={CustomGraphTooltip} isAnimationActive={true} />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey="name"
          interval={0}
          angle={-45}
          tick={(props) => CustomTickLabel(props, chart.data, isTiltedTickLabel)}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          dataKey="total"
          domain={[0, 'dataMax']}
          width="auto"
          niceTicks="adaptive"
          tick={{ fill: neutral300 }}
        />
        <Legend
          verticalAlign="bottom"
          wrapperStyle={{ bottom: 0, minHeight: 24 }}
          content={<CustomLegend platforms={platforms} getPlatformColor={getPlatformColor} />}
        />
        <For each={platforms}>
          {({ code, name }) => (
            <Bar
              dataKey={`platforms.${code}`}
              name={name}
              stackId="total"
              key={code}
              fill={getPlatformColor(code)}
              activeBar={false}
            />
          )}
        </For>
      </BarChart>
    </ChartWrapper>
  );
}
